import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BookOpen, ArrowLeft, ArrowRight, Download, FileText, Archive, Users, TrendingUp, Globe2, Award, Handshake, Scroll, Layers } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../context/I18nContext";
import SafeImage from "../components/SafeImage";
import Logo from "../components/Logo";

/**
 * MagazineReader — modernized page-by-page reconstruction of Édition N°0.
 *
 * The content below reproduces the original magazine's editorial structure
 * (client-owned content) in a bilingual FR/EN interface. Original PDF stays
 * accessible via the "Voir le PDF original" link.
 */

const SECTIONS = [
  { id: "cover", icon: Layers, key: "cover" },
  { id: "magazine", icon: BookOpen, key: "magazine" },
  { id: "readers", icon: Users, key: "readers" },
  { id: "market", icon: TrendingUp, key: "market" },
  { id: "distribution", icon: Globe2, key: "distribution" },
  { id: "brand", icon: Award, key: "brand" },
  { id: "partners", icon: Handshake, key: "partners" },
  { id: "communication", icon: Scroll, key: "communication" },
  { id: "archive", icon: Archive, key: "archive" },
];

const T = {
  fr: {
    back: "Retour au kiosque",
    pdf: "Voir le PDF original",
    heroKicker: "N°0 — Première édition historique",
    heroTitle: "Les origines du Magazine d'Affaires Panafricain",
    heroSub: "Découvrez l'édition fondatrice de Global Afrique International : sa ligne éditoriale, son positionnement panafricain, son lectorat, son approche des affaires et de la communication ainsi que les premières publications qui ont construit l'identité du magazine.",
    editorialLine: "Ligne éditoriale · Lectorat & diffusion · Tarifs publicitaires",
    labels: {
      cover: "Couverture",
      magazine: "Le Magazine",
      readers: "Les Lecteurs",
      market: "Le Marché",
      distribution: "La Distribution",
      brand: "La Marque",
      partners: "Les Partenaires",
      communication: "Communication & Régie",
      archive: "Archive éditoriale",
    },
    section: {
      magazine: {
        overline: "Chapitre I",
        title: "Le Magazine d'Affaires Panafricain",
        body: [
          "Avec plus de dix ans d'expérience de notre éditrice, Global Afrique International offre le magazine d'affaires, financier, politique, de modernisation et socio-culturel à vision internationale.",
          "Il est le repère en termes de couverture sur le plan des affaires, offrant à ses lecteurs des articles écrits d'un point de vue africain dans un environnement global. Son style, sobre et engagé, a pour objectif d'aider les hommes et les femmes d'affaires à travers le continent à mieux comprendre l'Afrique et ses opportunités.",
          "Global Afrique International bénéficie du respect de son lectorat grâce à l'excellence rédactionnelle et à sa contribution au développement de l'Afrique. Ses journalistes primés couvrent chaque mois les points économiques clés, ainsi que différents sujets et opportunités d'affaires sur le continent.",
          "Les classements annuels « Top Banques » et « Top Compagnies » sont devenus un indicateur essentiel du climat d'affaires, économique, politique, de modernisation et social sur le continent.",
        ],
      },
      readers: {
        overline: "Chapitre II",
        title: "Un lectorat de décideurs",
        body: [
          "Global Afrique International s'adresse aux acteurs les plus influents des mondes économique, politique et social — en Afrique comme à l'international.",
          "Nos lecteurs : hautes autorités politiques, dirigeants d'entreprises d'énergie et d'industrie, hauts fonctionnaires, banquiers, représentants d'organisations internationales et d'ONG.",
          "Le magazine se positionne comme une source d'information inestimable et un outil de compréhension des transformations africaines contemporaines.",
        ],
        pillars: [
          "Hautes autorités & institutions",
          "Dirigeants d'entreprises",
          "Banquiers & investisseurs",
          "Organisations internationales & ONG",
        ],
      },
      market: {
        overline: "Chapitre III",
        title: "Un marché en pleine expansion",
        body: [
          "L'Afrique est aujourd'hui l'un des marchés les plus dynamiques au monde. Global Afrique International accompagne cette dynamique en éclairant les décideurs sur les chiffres clés et les opportunités concrètes.",
        ],
        stats: [
          { k: "1 md", v: "de consommateurs sur le continent" },
          { k: "+100 M", v: "de consommateurs de classe moyenne" },
          { k: "#1", v: "mondial pour la croissance du mobile" },
          { k: "31 Md$", v: "d'investissements directs étrangers (2005)*" },
        ],
        note: "* Chiffre cité dans l'édition fondatrice — repère historique.",
      },
      distribution: {
        overline: "Chapitre IV",
        title: "Une distribution panafricaine",
        body: [
          "Global Afrique International est diffusé dans plus de 100 pays et distribué dans les sections spécialisées « affaires », les salons d'aéroports et les principaux hôtels du continent.",
          "Nos partenaires historiques de diffusion incluent les grandes compagnies aériennes africaines et internationales.",
        ],
        airlines: [
          "British Airways", "South African Airways", "Lufthansa", "Kenya Airways",
          "Ghana International", "Emirates", "Air Ethiopian", "Air Sénégal",
          "Tunis Air", "Air Seychelles", "Air Zimbabwe", "Nigeria Belview",
        ],
      },
      brand: {
        overline: "Chapitre V",
        title: "Une marque, un espace unique",
        body: [
          "Global Afrique International représente un espace unique dans l'univers média et éditorial du continent.",
          "Nous associons notre marque aux compagnies, dirigeants et institutions qui construisent l'Afrique de demain — partenaires capables de nous rendre collectivement plus forts.",
          "La croissance vient de nous-mêmes.",
        ],
      },
      partners: {
        overline: "Chapitre VI",
        title: "Nos partenaires historiques",
        body: [
          "L'Édition N°0 met à l'honneur les partenaires qui ont accompagné les premiers pas du magazine.",
        ],
        list: [
          "KS Production",
          "BBPick",
          "Air France",
          "AGECOM",
          "Prospective d'Avenir",
          "Club Amis BrazKin",
        ],
        note: "Partenaires cités dans l'édition fondatrice. Pour intégrer les partenaires actuels, contacter la régie.",
      },
      communication: {
        overline: "Chapitre VII",
        title: "Communication & Régie",
        body: [
          "Notre département Communication & Régie accompagne les entreprises, institutions, organisations et personnalités dans leur visibilité et leur stratégie de communication.",
          "Communication institutionnelle, visibilité médiatique, publicité, publireportage, couverture d'événements, contenus éditoriaux, communication digitale et valorisation d'image.",
          "Notre engagement : donner de la visibilité à ceux qui construisent, entreprennent, innovent et contribuent au rayonnement de l'Afrique.",
        ],
        cta: "Découvrir nos offres actuelles",
      },
      archive: {
        overline: "Archive",
        title: "Grille tarifaire historique — 2017/2018",
        body: [
          "La grille publicitaire ci-dessous est reproduite telle qu'elle figurait dans l'édition fondatrice. Elle a une valeur historique et documentaire ; elle ne représente pas les tarifs commerciaux actuels.",
          "Pour les tarifs en vigueur, rendez-vous sur la page « Annoncer dans GAI » ou demandez un devis personnalisé.",
        ],
        headers: ["Format", "Dimensions (L × H mm)", "Quadrichromie", "Noir & Blanc"],
        rows: [
          ["Double page couverture intérieure", "420 × 297", "6 500 €", "—"],
          ["Double page", "420 × 297", "5 240 €", "—"],
          ["4ᵉ de couverture", "210 × 297", "3 200 €", "—"],
          ["2ᵉ ou 3ᵉ de couverture", "210 × 297", "3 000 €", "2 500 €"],
          ["Page", "210 × 297", "2 500 €", "1 250 €"],
          ["½ Page", "190 × 86 / 125 × 168", "1 500 €", "1 000 €"],
          ["⅓ Page", "190 × 86 / 60 × 238", "1 000 €", "750 €"],
          ["¼ Page", "125 × 100 / 91 × 132", "750 €", "500 €"],
          ["Bandeaux (3 pages)", "190 × 35", "2 500 €", "1 250 €"],
        ],
        articleTitle: "Article d'archive · Les actions humanitaires de Muana Ndeya (le fils de l'espoir)",
        articleAuthor: "Denis Christel Sassou Nguesso — Fondation Perspectives d'Avenir",
        articleSummary: "L'édition fondatrice publiait un dossier sur les actions humanitaires menées via la Fondation Perspectives d'Avenir : dépistage et prévention du cancer du col de l'utérus à Brazzaville, Loandjili et Oyo (2013-2014), formation professionnelle des jeunes (FOUP, Ledger Plaza Maya-Maya, août 2014) et inauguration du siège de la fondation en février 2016.",
        articleTag: "Archive éditoriale · reproduit à titre historique",
      },
    },
  },
  en: {
    back: "Back to the kiosk",
    pdf: "View original PDF",
    heroKicker: "N°0 — First historical edition",
    heroTitle: "The origins of the Pan-African Business Magazine",
    heroSub: "Discover the founding edition of Global Afrique International: its editorial line, its pan-African positioning, its readership, its approach to business and communication, and the first publications that shaped the magazine's identity.",
    editorialLine: "Editorial line · Readership & distribution · Advertising rates",
    labels: {
      cover: "Cover",
      magazine: "The Magazine",
      readers: "The Readers",
      market: "The Market",
      distribution: "Distribution",
      brand: "The Brand",
      partners: "The Partners",
      communication: "Communication & Régie",
      archive: "Editorial archive",
    },
    section: {
      magazine: {
        overline: "Chapter I",
        title: "The Pan-African Business Magazine",
        body: [
          "With over ten years of experience from our editor, Global Afrique International delivers a business, financial, political, modernisation and socio-cultural magazine with a truly international vision.",
          "It is a reference in business coverage, offering readers articles written from an African perspective in a global environment. Its sober and committed style helps business leaders across the continent better understand Africa and its opportunities.",
          "Global Afrique International is respected for its editorial excellence and its contribution to Africa's development. Award-winning journalists cover key economic issues every month, along with a wide range of business topics and opportunities.",
          "The annual rankings 'Top Banks' and 'Top Companies' have become essential indicators of the business, economic, political, modernisation and social climate on the continent.",
        ],
      },
      readers: {
        overline: "Chapter II",
        title: "A readership of decision-makers",
        body: [
          "Global Afrique International speaks to the most influential players in economics, politics and society — in Africa and abroad.",
          "Our readers: high political authorities, leaders of energy and industry, senior officials, bankers, representatives of international organisations and NGOs.",
          "The magazine is positioned as an invaluable source of information and a tool for understanding Africa's ongoing transformations.",
        ],
        pillars: [
          "High authorities & institutions",
          "Business executives",
          "Bankers & investors",
          "International organisations & NGOs",
        ],
      },
      market: {
        overline: "Chapter III",
        title: "A rapidly expanding market",
        body: [
          "Africa is today one of the most dynamic markets in the world. Global Afrique International supports this dynamic by informing decision-makers with the key figures and concrete opportunities.",
        ],
        stats: [
          { k: "1 bn", v: "consumers on the continent" },
          { k: "+100 M", v: "middle-class consumers" },
          { k: "#1", v: "worldwide for mobile growth" },
          { k: "$31 Bn", v: "in FDI (2005)*" },
        ],
        note: "* Figure quoted in the founding edition — historical benchmark.",
      },
      distribution: {
        overline: "Chapter IV",
        title: "Pan-African distribution",
        body: [
          "Global Afrique International is distributed in more than 100 countries, in business specialised sections, airport lounges and major hotels across the continent.",
          "Historical distribution partners include major African and international airlines.",
        ],
        airlines: [
          "British Airways", "South African Airways", "Lufthansa", "Kenya Airways",
          "Ghana International", "Emirates", "Air Ethiopian", "Air Sénégal",
          "Tunis Air", "Air Seychelles", "Air Zimbabwe", "Nigeria Belview",
        ],
      },
      brand: {
        overline: "Chapter V",
        title: "A brand, a unique space",
        body: [
          "Global Afrique International represents a unique space in the continent's media and publishing landscape.",
          "We associate our brand with the companies, leaders and institutions building tomorrow's Africa — partners who make us collectively stronger.",
          "Growth comes from within.",
        ],
      },
      partners: {
        overline: "Chapter VI",
        title: "Our historical partners",
        body: [
          "Edition N°0 honours the partners who accompanied the magazine's earliest steps.",
        ],
        list: [
          "KS Production",
          "BBPick",
          "Air France",
          "AGECOM",
          "Prospective d'Avenir",
          "Club Amis BrazKin",
        ],
        note: "Partners listed in the founding edition. To onboard current partners, contact the sales desk.",
      },
      communication: {
        overline: "Chapter VII",
        title: "Communication & Régie",
        body: [
          "Our Communication & Régie department supports companies, institutions, organisations and personalities in their visibility and communication strategy.",
          "Institutional communication, media visibility, advertising, advertorials, event coverage, editorial content, digital communication and image building.",
          "Our commitment: to give visibility to those who build, undertake, innovate and contribute to Africa's influence.",
        ],
        cta: "See our current offers",
      },
      archive: {
        overline: "Archive",
        title: "Historical rate card — 2017/2018",
        body: [
          "The rate card below is reproduced as it appeared in the founding edition. It is documented for historical purposes and does not represent current commercial rates.",
          "For up-to-date rates, visit the 'Advertise in GAI' page or request a custom quote.",
        ],
        headers: ["Format", "Dimensions (W × H mm)", "Full colour", "Black & White"],
        rows: [
          ["Inside cover double page", "420 × 297", "€6,500", "—"],
          ["Double page", "420 × 297", "€5,240", "—"],
          ["Back cover", "210 × 297", "€3,200", "—"],
          ["Inside front / back cover", "210 × 297", "€3,000", "€2,500"],
          ["Full page", "210 × 297", "€2,500", "€1,250"],
          ["½ Page", "190 × 86 / 125 × 168", "€1,500", "€1,000"],
          ["⅓ Page", "190 × 86 / 60 × 238", "€1,000", "€750"],
          ["¼ Page", "125 × 100 / 91 × 132", "€750", "€500"],
          ["Banners (3 pages)", "190 × 35", "€2,500", "€1,250"],
        ],
        articleTitle: "Archive article · The humanitarian actions of Muana Ndeya",
        articleAuthor: "Denis Christel Sassou Nguesso — Perspectives d'Avenir Foundation",
        articleSummary: "The founding edition featured a dossier on humanitarian actions led by the Perspectives d'Avenir Foundation: cervical cancer screening and prevention in Brazzaville, Loandjili and Oyo (2013-2014), professional training for youth (FOUP forum, Ledger Plaza Maya-Maya, August 2014) and the official inauguration of the foundation's headquarters in February 2016.",
        articleTag: "Editorial archive · reproduced for historical reference",
      },
    },
  },
};

export default function MagazineReader() {
  const { id } = useParams();
  const { lang } = useI18n();
  const t = T[lang] || T.fr;
  const [mag, setMag] = useState(null);
  const [active, setActive] = useState("cover");
  const refs = useRef({});

  useEffect(() => {
    api.get(`/magazines/${id}`).then((r) => setMag(r.data)).catch(() => setMag(null));
  }, [id]);

  // Scroll spy
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) setActive(vis[0].target.dataset.section);
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: [0.1, 0.4, 0.75] }
    );
    Object.values(refs.current).forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [mag]);

  const scrollTo = (id) => {
    const el = refs.current[id];
    if (!el) return;
    window.scrollTo({ top: el.offsetTop - 90, behavior: "smooth" });
  };

  const activeIdx = useMemo(() => SECTIONS.findIndex((s) => s.id === active), [active]);
  const next = SECTIONS[activeIdx + 1];
  const prev = SECTIONS[activeIdx - 1];

  const bind = (id) => ({
    ref: (el) => (refs.current[id] = el),
    "data-section": id,
    "data-testid": `reader-section-${id}`,
    className: "scroll-mt-24",
  });

  const S = t.section;

  return (
    <div className="bg-[var(--gai-white)] min-h-screen" data-testid="page-magazine-reader">
      {/* Top bar */}
      <div className="sticky top-0 z-40 border-b border-[var(--gai-grey-200)] bg-[var(--gai-white)]/95 backdrop-blur">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 h-14 flex items-center justify-between gap-4">
          <Link to="/magazine" className="text-[0.68rem] tracking-[0.22em] uppercase text-[var(--gai-grey-600)] hover:text-[var(--gai-red)]" data-testid="reader-back">
            <ArrowLeft size={12} className="inline mr-1.5" /> {t.back}
          </Link>
          <div className="hidden md:flex items-center gap-2">
            <Logo size={30} />
            <span className="text-[0.68rem] tracking-[0.22em] uppercase text-[var(--gai-red)] font-semibold" translate="no">
              Édition N°0
            </span>
          </div>
          {mag?.pdf_url && (
            <Link to={`/magazine/${id}/pdf`} className="text-[0.68rem] tracking-[0.22em] uppercase text-[var(--gai-black)] hover:text-[var(--gai-red)]" data-testid="reader-open-pdf">
              <FileText size={12} className="inline mr-1.5" /> {t.pdf}
            </Link>
          )}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-10 grid lg:grid-cols-12 gap-10">
        {/* Sticky sidebar TOC */}
        <aside className="hidden lg:block lg:col-span-3 lg:sticky lg:top-24 self-start" data-testid="reader-toc">
          <div className="overline mb-4 text-[var(--gai-red)]">
            {lang === "fr" ? "Sommaire" : "Contents"}
          </div>
          <ol className="space-y-1">
            {SECTIONS.map((s, i) => {
              const isActive = s.id === active;
              return (
                <li key={s.id}>
                  <button
                    onClick={() => scrollTo(s.id)}
                    className={`w-full text-left flex items-center gap-3 px-3 py-2 text-sm border-l-2 transition-all ${
                      isActive
                        ? "border-[var(--gai-red)] text-[var(--gai-black)] bg-[var(--gai-grey-50)]"
                        : "border-transparent text-[var(--gai-grey-600)] hover:text-[var(--gai-black)]"
                    }`}
                    data-testid={`reader-toc-${s.id}`}
                  >
                    <span className={`text-[0.62rem] tracking-[0.2em] ${isActive ? "text-[var(--gai-red)]" : "text-[var(--gai-grey-500)]"}`}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="flex-1">{t.labels[s.key]}</span>
                  </button>
                </li>
              );
            })}
          </ol>

          <div className="mt-8 p-4 border border-[var(--gai-grey-200)] bg-[var(--gai-grey-50)]">
            <div className="overline mb-2 text-[var(--gai-red)]">
              {lang === "fr" ? "Progression" : "Progress"}
            </div>
            <div className="h-1 bg-[var(--gai-grey-200)] overflow-hidden">
              <div
                className="h-full bg-[var(--gai-red)] transition-all"
                style={{ width: `${((activeIdx + 1) / SECTIONS.length) * 100}%` }}
              />
            </div>
            <div className="mt-2 text-[0.72rem] text-[var(--gai-grey-600)]">
              {activeIdx + 1} / {SECTIONS.length}
            </div>
          </div>
        </aside>

        {/* Main editorial */}
        <main className="lg:col-span-9 space-y-24">
          {/* COVER */}
          <section {...bind("cover")}>
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="overline text-[var(--gai-red)] mb-4">{t.heroKicker}</div>
                <h1 className="serif text-4xl md:text-6xl leading-[1.02] text-[var(--gai-black)]">
                  {t.heroTitle}
                </h1>
                <div className="mt-6 h-[2px] w-24 bg-[var(--gai-red)]" aria-hidden />
                <p className="mt-6 text-[1.02rem] leading-[1.85] text-[var(--gai-grey-800)]">
                  {t.heroSub}
                </p>
                <div className="mt-6 text-[0.72rem] tracking-[0.22em] uppercase text-[var(--gai-grey-500)]" translate="no">
                  {t.editorialLine}
                </div>
                <div className="mt-8">
                  <Logo size={80} />
                </div>
              </div>
              <div className="relative">
                <div className="absolute -inset-4 border border-[var(--gai-grey-300)]" aria-hidden />
                <SafeImage
                  src={mag?.cover_url}
                  alt="Édition N°0"
                  className="relative w-full object-cover"
                  style={{ aspectRatio: "3/4" }}
                  loading="eager"
                  testid="reader-cover-image"
                />
                <div className="absolute top-3 left-3 px-3 py-1.5 bg-[var(--gai-red)] text-[var(--gai-white)] text-[0.62rem] tracking-[0.22em] uppercase font-bold" translate="no">
                  N°0
                </div>
              </div>
            </div>
          </section>

          {/* MAGAZINE */}
          <ChapterBlock {...bind("magazine")} data={S.magazine} />

          {/* READERS */}
          <ChapterBlock {...bind("readers")} data={S.readers}>
            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {S.readers.pillars.map((p, i) => (
                <div key={i} className="p-5 border border-[var(--gai-grey-200)] bg-[var(--gai-white)]">
                  <div className="text-[0.65rem] tracking-[0.22em] uppercase text-[var(--gai-red)] mb-2">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="text-[var(--gai-black)] text-base">{p}</div>
                </div>
              ))}
            </div>
          </ChapterBlock>

          {/* MARKET */}
          <ChapterBlock {...bind("market")} data={S.market}>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 border border-[var(--gai-grey-200)]">
              {S.market.stats.map((st, i) => (
                <div key={i} className={`p-6 ${i < 3 ? "md:border-r border-[var(--gai-grey-200)]" : ""} ${i < 2 ? "border-b md:border-b-0" : ""}`}>
                  <div className="serif text-4xl md:text-5xl leading-none text-[var(--gai-red)]" translate="no">{st.k}</div>
                  <div className="mt-3 text-[0.72rem] tracking-[0.18em] uppercase text-[var(--gai-grey-700)] leading-relaxed">
                    {st.v}
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[0.72rem] italic text-[var(--gai-grey-500)]">{S.market.note}</p>
          </ChapterBlock>

          {/* DISTRIBUTION */}
          <ChapterBlock {...bind("distribution")} data={S.distribution}>
            <div className="mt-8">
              <div className="overline mb-3 text-[var(--gai-red)]">
                {lang === "fr" ? "Compagnies aériennes partenaires (historique)" : "Airline distribution partners (historical)"}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2">
                {S.distribution.airlines.map((a) => (
                  <div key={a} className="text-sm text-[var(--gai-grey-800)] flex items-center gap-2 py-1.5 border-b border-[var(--gai-grey-100)]" translate="no">
                    <span className="w-1 h-1 bg-[var(--gai-red)]" />
                    {a}
                  </div>
                ))}
              </div>
            </div>
          </ChapterBlock>

          {/* BRAND */}
          <ChapterBlock {...bind("brand")} data={S.brand} />

          {/* PARTNERS */}
          <ChapterBlock {...bind("partners")} data={S.partners}>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-3">
              {S.partners.list.map((p) => (
                <div key={p} className="p-5 border border-[var(--gai-grey-200)] bg-[var(--gai-white)] text-center" translate="no">
                  <div className="serif text-lg text-[var(--gai-black)]">{p}</div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[0.72rem] italic text-[var(--gai-grey-500)]">{S.partners.note}</p>
          </ChapterBlock>

          {/* COMMUNICATION */}
          <ChapterBlock {...bind("communication")} data={S.communication}>
            <Link to="/annoncer" className="mt-8 gai-btn-gold inline-flex" data-testid="reader-cta-annoncer">
              {S.communication.cta} <ArrowRight size={14} />
            </Link>
          </ChapterBlock>

          {/* ARCHIVE (rate card + historical article) */}
          <section {...bind("archive")}>
            <div className="overline text-[var(--gai-grey-500)]">{S.archive.overline}</div>
            <h2 className="serif text-3xl md:text-4xl mt-3 text-[var(--gai-black)]">{S.archive.title}</h2>
            <div className="mt-6 h-[2px] w-16 bg-[var(--gai-red)]" aria-hidden />

            <div className="mt-6 space-y-4 text-[1rem] leading-[1.85] text-[var(--gai-grey-800)]">
              {S.archive.body.map((p, i) => <p key={i}>{p}</p>)}
            </div>

            {/* Historical rate table */}
            <div className="mt-8 overflow-x-auto border border-[var(--gai-grey-200)]">
              <table className="min-w-full text-sm" data-testid="reader-archive-table">
                <thead>
                  <tr className="bg-[var(--gai-black)] text-[var(--gai-white)] text-[0.68rem] tracking-[0.2em] uppercase">
                    {S.archive.headers.map((h) => (
                      <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {S.archive.rows.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? "bg-[var(--gai-white)]" : "bg-[var(--gai-grey-50)]"}>
                      {row.map((cell, j) => (
                        <td key={j} className={`px-4 py-3 border-t border-[var(--gai-grey-100)] ${j === 0 ? "font-medium text-[var(--gai-black)]" : "text-[var(--gai-grey-800)]"}`} translate="no">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Historical article summary */}
            <div className="mt-10 p-6 md:p-8 border-l-4 border-[var(--gai-red)] bg-[var(--gai-grey-50)]">
              <div className="text-[0.62rem] tracking-[0.22em] uppercase text-[var(--gai-red)] font-bold mb-3">
                {S.archive.articleTag}
              </div>
              <h3 className="serif text-2xl md:text-3xl text-[var(--gai-black)]">
                {S.archive.articleTitle}
              </h3>
              <div className="mt-2 text-sm text-[var(--gai-grey-600)] italic" translate="no">
                {S.archive.articleAuthor}
              </div>
              <p className="mt-5 text-[1rem] leading-[1.85] text-[var(--gai-grey-800)]">
                {S.archive.articleSummary}
              </p>
            </div>

            {/* End nav */}
            <div className="mt-12 pt-8 border-t border-[var(--gai-grey-200)] flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="overline text-[var(--gai-grey-500)]">
                  {lang === "fr" ? "Vous avez terminé" : "You've reached the end"}
                </div>
                <div className="serif text-xl mt-1 text-[var(--gai-black)]" translate="no">
                  Édition N°0 — Global Afrique International
                </div>
              </div>
              <div className="flex gap-3">
                {mag?.pdf_url && (
                  <a href={/^https?:\/\//i.test(mag.pdf_url) ? mag.pdf_url : `${process.env.REACT_APP_BACKEND_URL}${mag.pdf_url}`} target="_blank" rel="noreferrer" className="gai-btn-outline" data-testid="reader-download-pdf">
                    <Download size={14} /> PDF
                  </a>
                )}
                <Link to="/magazine" className="gai-btn-gold" data-testid="reader-back-cta">
                  {t.back}
                </Link>
              </div>
            </div>
          </section>
        </main>
      </div>

      {/* Floating prev/next — bottom-left to avoid preview badge overlap */}
      <div className="fixed bottom-6 left-6 z-[60] flex flex-col gap-2" data-testid="reader-nav">
        {prev && (
          <button
            onClick={() => scrollTo(prev.id)}
            className="w-12 h-12 flex items-center justify-center bg-[var(--gai-white)] border border-[var(--gai-grey-300)] text-[var(--gai-black)] hover:bg-[var(--gai-black)] hover:text-[var(--gai-white)] transition-colors shadow-lg"
            aria-label={t.labels[prev.key]}
            data-testid="reader-prev"
          >
            <ArrowLeft size={16} />
          </button>
        )}
        {next && (
          <button
            onClick={() => scrollTo(next.id)}
            className="w-12 h-12 flex items-center justify-center bg-[var(--gai-red)] text-[var(--gai-white)] hover:opacity-90 transition-opacity shadow-lg"
            aria-label={t.labels[next.key]}
            data-testid="reader-next"
          >
            <ArrowRight size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

function ChapterBlock({ data, children, ...rest }) {
  return (
    <section {...rest}>
      <div className="overline text-[var(--gai-grey-500)]">{data.overline}</div>
      <h2 className="serif text-3xl md:text-5xl mt-3 leading-[1.05] text-[var(--gai-black)]">
        {data.title}
      </h2>
      <div className="mt-6 h-[2px] w-16 bg-[var(--gai-red)]" aria-hidden />
      <div className="mt-6 space-y-5 text-[1.02rem] leading-[1.85] text-[var(--gai-grey-800)]">
        {data.body.map((p, i) => <p key={i}>{p}</p>)}
      </div>
      {children}
    </section>
  );
}
